import React, { FC, useCallback } from 'react';
import { HeaderGroup } from 'react-table';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { getControlImages } from 'store/selectors/controls.selector';
import { getUser } from 'store/selectors/auth.selector';
import { getIsModalsDisabled } from 'store/selectors/ui.selector';
import { useModalState } from 'modules/core/hooks';
import { MODAL_DIALOG_ID } from 'modules/core/constants';
import { DisabledModals } from 'modules/core/enums/disabledModals';
import { IUser } from 'modules/core/types';

import { AddInventoryBannerCell, AddInventoryBannerContent, DemoTagSimple, UpgradeIcon } from '../styles';
import { ChevronDown, ChevronUp } from '../../Icons';
import { TABLE_RENDER_HEADER_KEY } from '../table.const';
import { TableHeaderRowElement, TableHeaderCell, TableHeaderCellDiv, StickyThead } from './styles';

export interface ITableHeader {
  headerGroups: HeaderGroup[];
}

export const TableHeader: FC<ITableHeader> = ({ headerGroups }) => {
  const { t } = useTranslation();
  const user = useSelector(getUser) as IUser;
  const isUnlockInventoryDisabled = useSelector(getIsModalsDisabled);
  const controlImages = useSelector(getControlImages);
  const { handleOpenDialog: handleOpenUnlockInventory } = useModalState([MODAL_DIALOG_ID.UNLOCK_INVENTORY]);
  const unlockDisabled = isUnlockInventoryDisabled[DisabledModals.UNLOCK_INVENTORY];

  const handleOpenUpgradeInventory = useCallback(() => {
    if (controlImages.unlockInventoryUrl) {
      window.open(controlImages.unlockInventoryUrl, '_blank');
    } else {
      handleOpenUnlockInventory();
    }
  }, [handleOpenUnlockInventory, controlImages.unlockInventoryUrl]);

  const showUpgradeInventoryBanner =
    !user.isWithInventory && !(user?.foureyesWebsiteId && user?.withWholesaleInventory);

  return (
    <StickyThead data-testid="vehicle-table-header">
      {headerGroups.map(({ getHeaderGroupProps, headers }) => (
        <TableHeaderRowElement {...getHeaderGroupProps()}>
          {headers.map(({ getHeaderProps, render, getSortByToggleProps, isSorted, isSortedDesc, canSort }) => {
            const sortedDescIcon = isSortedDesc ? <ChevronDown active /> : <ChevronUp />;
            const sortedIcon = isSorted ? sortedDescIcon : <ChevronDown />;

            return (
              <TableHeaderCell
                data-testid={render(TABLE_RENDER_HEADER_KEY)}
                {...getHeaderProps(getSortByToggleProps())}
              >
                <TableHeaderCellDiv>
                  {render(TABLE_RENDER_HEADER_KEY)}
                  {canSort && sortedIcon}
                </TableHeaderCellDiv>
              </TableHeaderCell>
            );
          })}
        </TableHeaderRowElement>
      ))}
      {showUpgradeInventoryBanner && (
        <tr>
          <AddInventoryBannerCell
            colSpan={100}
            role="button"
            tabIndex={0}
            $disabled={unlockDisabled}
            onClick={unlockDisabled ? undefined : handleOpenUpgradeInventory}
          >
            <AddInventoryBannerContent>
              <span data-product-fruits="upgrade-inventory-banner">
                <UpgradeIcon />
                {t('components.RACAffordabilityEngine.withoutInventory.inventoryCtas.upgradeMyInventory')}
              </span>
              <DemoTagSimple>
                {t('components.RACAffordabilityEngine.withoutInventory.inventoryCtas.clickHere')}
              </DemoTagSimple>
            </AddInventoryBannerContent>
          </AddInventoryBannerCell>
        </tr>
      )}
    </StickyThead>
  );
};
